import type { IssueTypes } from '@risksmart-app/trpc/src/services/service.types';
import type { UpdateIssueMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  namedOperations,
  UpdateIssueDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import type { UpdateIssueInput } from '@/hooks/mutations/nativeMutationInputs';
import { createMutationHook } from '@/utils/hook-factories';

interface UpdateIssueOptions {
  issueType: string;
}

// The tRPC procedure needs the issue Type — a hook-level option on the public
// hook, folded into the mutate input by the binding wrapper below. The Apollo
// document doesn't take it, so `graphqlVariables` strips it again.
const useUpdateIssueBase = createMutationHook({
  name: 'updateIssue',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.issue.update.mutationOptions({ onSuccess }),
  mapInput: (input: UpdateIssueInput & { Type: IssueTypes }) => input,
  invalidate: (trpc) => [
    trpc.frontend.issue.register.queryKey(),
    trpc.frontend.issue.issueById.queryKey(),
    trpc.frontend.issue.issuesByParentId.queryKey(),
    trpc.frontend.issue.issueAssessmentByParentId.queryKey(),
    // COI-889: linked-item reads embed the linked entity's title
    // (`linked-item.query.ts`), which this hook rewrites.
    trpc.frontend.linkedItem.linkedItems.queryKey(),
    // COI-889: the my-items ribbon counts this entity.
    trpc.frontend.myItems.pathKey(),
  ],
  mapResponse: (): UpdateIssueMutation => ({
    updateIssueApi: {
      __typename: 'GenericMutationOutput',
      affected_rows: 1,
    },
  }),
  graphqlDocument: UpdateIssueDocument,
  graphqlVariables: ({
    Type: _Type,
    ...variables
  }: UpdateIssueInput & { Type: IssueTypes }) => ({ object: variables }),
  evictFields: ['issue'],
  refetchQueries: [namedOperations.Query.getIssueById],
});

/**
 * Binds the hook-level `issueType` option onto the factory hook's mutate input
 * so consumers keep the existing `useUpdateIssue({ issueType })` signature.
 */
export const useUpdateIssue = ({ issueType }: UpdateIssueOptions) => {
  const { updateIssue, loading, error } = useUpdateIssueBase();

  return {
    updateIssue: async (variables: UpdateIssueInput): Promise<void> => {
      await updateIssue({ ...variables, Type: issueType as IssueTypes });
    },
    loading,
    error,
  };
};
